import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../services/auth/useApi";
import { AxiosError } from "axios";
import { CreateSpaceType, SpaceType } from "../../../types/space";
import { inProgressSpaceListState } from "../../../recoil/spaces/inProgressSpaceListState";
import { useRecoilState } from "recoil";
import { SpaceKind, SpaceStatus } from "../../../types/space/enum";
import { spaceQueryKeys } from "../queries";
import { usePreference } from "../../../hooks/preference/usePreference";

export const createSpaceApi = async (
  api: ReturnType<typeof useApi>,
  payload: CreateSpaceType
): Promise<SpaceType> => {
  const response = await api.post("/spaces", payload);
  return response.data;
};

export const useCreateSpaceMutation = (
  options?: UseMutationOptions<SpaceType, AxiosError, CreateSpaceType>
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [inProgressSpaceList, setInProgressSpaceList] = useRecoilState(inProgressSpaceListState);
  const { updatePreference } = usePreference();

  return useMutation<SpaceType, AxiosError, CreateSpaceType>({
    mutationFn: (payload) => createSpaceApi(api, payload),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: spaceQueryKeys.status(SpaceStatus.InProgress) });
    },
    onSuccess: (res: SpaceType) => {
      // 새로운 리스트 생성
      const newList = inProgressSpaceList ? [res, ...inProgressSpaceList] : [res];
      // 전체 inProgressSpaceList 상태 업데이트
      setInProgressSpaceList(newList);

      // selectedSpaceIds 업데이트
      const normalAndDefaultInProgressSpaceList =
        newList?.filter((space) => space.spaceType !== SpaceKind.Divider) ?? [];
      updatePreference({
        selectedSpaceIds: normalAndDefaultInProgressSpaceList?.map((space) => space.id),
      });

      queryClient.invalidateQueries({ queryKey: spaceQueryKeys.status(SpaceStatus.InProgress) });

      return { inProgressSpaceList };
    },
    ...options,
  });
};
